import {
  CustomDateRangePicker,
  DataTable,
  formatDataToLabelValue,
  getDateOfTheBeginningOfCurrentMonth,
  getDateOfToday,
  IOptions,
  MainButton,
  ModalActivityDetails,
  ModalConfirmAction,
  Select,
  STATUSPAGE,
} from '@repo/ui';
import {
  GetAllCoursesByPersonUseCaseInterface,
  GetAllEntitiesByPersonUseCaseInterface,
  GetAllPersonProfileUseCaseInterface,
} from 'domain/usecases';
import {
  ContentState,
  ContentStateStatus,
} from 'presentation/app/components/content-state';
import { EntityAndCourseFiltersByProfiles } from 'presentation/app/components/entity-and-course-filters-by-profiles';
import {
  useEntityAndCourseFiltersByProfiles,
  useEntityAndCourseFiltersTouched,
} from 'presentation/app/hooks/entity-and-course-filters';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Label, Row } from 'reactstrap';

import endActivity from '../../../../../assets/images/activity-details-icon/activity-end-icon.png';
import startActivity from '../../../../../assets/images/activity-details-icon/activity-start-icon.png';
import noDataBg from '../../../../../assets/images/no-task.png';
import { makeColumnsPersonValidatedActivities } from '../../../common/columns/columns-person-validated-activities';
import { usePersonValidatedActivitiesTab } from '../hooks';

interface IPersonValidatedActivitiesTabProps {
  profiles: GetAllPersonProfileUseCaseInterface.Result;
  courses: GetAllCoursesByPersonUseCaseInterface.Result;
  organizations: GetAllEntitiesByPersonUseCaseInterface.Result;
}

export const PersonValidatedActivitiesTab = ({
  courses,
  profiles,
  organizations,
}: IPersonValidatedActivitiesTabProps) => {
  const [period, setPeriod] = useState<Date[]>([
    getDateOfTheBeginningOfCurrentMonth(),
    getDateOfToday(),
  ]);
  const [selectedGroups, setSelectedGroups] = useState<IOptions[]>([]);
  const [isModalActivityOpen, setIsModalActivityOpen] =
    useState<boolean>(false);
  const [isConfirmDeleteValidationModalOpen, setIsConfirmValidationModalOpen] =
    useState<boolean>(false);
  const [selectedActivityId, setSelectedActivityId] = useState<string | null>(
    null,
  );

  const { id } = useParams();

  const { touched, validate } = useEntityAndCourseFiltersTouched();
  const {
    groups,
    activity,
    requestStatus,
    loadingGroups,
    loadingActivity,
    validatedActivities,
    requestGetActivityById,
    requestGetAllGroupsUser,
    loadingValidatedActivities,
    loadingDeleteActivityValidation,
    requestDeleteActivityValidation,
    requestGetAllPersonValidatedActivities,
  } = usePersonValidatedActivitiesTab();

  const {
    orgOptions,
    courseOptions,
    selectedCourse,
    onCourseChange,
    hasValidProfile,
    selectedOrganization,
    onOrganizationChange,
  } = useEntityAndCourseFiltersByProfiles({
    courses,
    organizations,
    profiles,
  });
  const contentStateStatus: ContentStateStatus | null =
    requestStatus === STATUSPAGE.DONE
      ? validatedActivities.length > 0
        ? null
        : STATUSPAGE.DONE
      : requestStatus;

  const toggleModalActivities = () => {
    setIsModalActivityOpen(!isModalActivityOpen);
  };

  const toggleConfirmDeleteValidationModal = () => {
    setIsConfirmValidationModalOpen(!isConfirmDeleteValidationModalOpen);
  };

  const handleFiltersSubmit = () => {
    if (!validate(selectedOrganization, selectedCourse)) return;

    const filtersToSubmit = {
      id,
      courseHashId: selectedCourse?.value || '',
      organizationHashId: selectedOrganization?.value || '',
      ['groupHashIds[]']: selectedGroups.map((group) => group.value),
      startsAt: period[0].toISOString(),
      endsAt: period[1].toISOString(),
    };

    requestGetAllPersonValidatedActivities(filtersToSubmit);
  };

  useEffect(() => {
    requestGetAllGroupsUser({
      courseId: selectedCourse?.value || '',
      entityId: selectedOrganization?.value || '',
    });
  }, [selectedCourse, selectedOrganization]);

  return (
    <Col md={12} className="mt-5">
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          handleFiltersSubmit();
        }}
      >
        <Card className="p-3">
          <Row className="g-3">
            <EntityAndCourseFiltersByProfiles
              hasValidProfile={hasValidProfile}
              orgColSize={3}
              courseColSize={3}
              courses={courseOptions}
              organizations={orgOptions}
              selectedCourse={selectedCourse}
              selectedOrganization={selectedOrganization}
              onOrganizationChange={(organization) => {
                onOrganizationChange(organization);
                setSelectedGroups([]);
              }}
              onCourseChange={(course) => {
                onCourseChange(course);
                setSelectedGroups([]);
              }}
              touched={touched}
            />

            <Col md={3}>
              <Label className="form-label">Grupos</Label>
              <Select
                isMulti
                isClearable
                id="groups"
                name="groups"
                onChange={(selected) => {
                  setSelectedGroups((selected ?? []) as IOptions[]);
                }}
                placeholder="Todos os grupos"
                options={formatDataToLabelValue(groups as never, 'id', 'nome')}
                isLoading={loadingGroups}
                // ajustar tipagem de componente Select para receber IOptions[] também
                // (em casos de isMulti)
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                value={selectedGroups as any}
              />
            </Col>

            <Col md={3}>
              <Row>
                <Label htmlFor="period" className="form-label">
                  Período
                </Label>
                <CustomDateRangePicker
                  initialStartDate={new Date(period[0])}
                  initialEndDate={new Date(period[1])}
                  cleanable={false}
                  placement="auto"
                  value={
                    period.length > 0
                      ? [new Date(period[0]), new Date(period[1])]
                      : null
                  }
                  menuClassName="custom-datepicker-menu"
                  onChange={(e: Date[]) => {
                    setPeriod(e);
                  }}
                />
              </Row>
            </Col>

            <Row>
              <Col
                md={12}
                className="d-flex align-items-center justify-content-end mt-4 mx-4 gap-3"
              >
                <MainButton
                  type="submit"
                  isLoading={loadingValidatedActivities}
                >
                  Filtrar
                </MainButton>
              </Col>
            </Row>
          </Row>
        </Card>
      </Form>

      {contentStateStatus !== null && (
        <ContentState
          status={contentStateStatus}
          className="mt-3"
          action={
            contentStateStatus === STATUSPAGE.ERROR
              ? {
                  label: 'Tentar novamente',
                  icon: 'ri-refresh-line',
                  onClick: handleFiltersSubmit,
                }
              : undefined
          }
        />
      )}

      {requestStatus === STATUSPAGE.DONE && validatedActivities?.length > 0 && (
        <Card className="p-3 pt-0">
          <DataTable
            actions={[
              {
                id: 1,
                name: 'view',
                label: 'Visualizar detalhes da atividade',
                icon: 'ri-file-search-line',
                classNameColorIcon: 'none',
                onPress: (cellProps) => {
                  requestGetActivityById({ id: cellProps.id });
                  toggleModalActivities();
                },
              },
            ]}
            canSearch
            data={validatedActivities || []}
            columns={makeColumnsPersonValidatedActivities()}
            loading={loadingValidatedActivities}
            defaultSortAsc
            defaultSortFieldId={1}
            noDataBg={noDataBg}
          />
        </Card>
      )}

      <ModalActivityDetails
        isOpen={isModalActivityOpen}
        toggle={toggleModalActivities}
        activity={activity}
        loading={loadingActivity}
        footerClassName="justify-content-between"
        additionalBtnsToFooter={
          <Button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              setSelectedActivityId(activity.id);
              toggleConfirmDeleteValidationModal();
            }}
          >
            Anular Validação
          </Button>
        }
        mapAssets={{
          startActivity,
          endActivity,
        }}
      />

      <ModalConfirmAction
        zIndex={10000}
        modalTheme="danger"
        isOpen={isConfirmDeleteValidationModalOpen}
        toggle={toggleConfirmDeleteValidationModal}
        title="Anular Validação?"
        subtitle="Tem certeza que deseja anular a validação desta atividade?"
        onConfirm={() =>
          requestDeleteActivityValidation(
            {
              activityId: selectedActivityId,
            },
            () => {
              toggleConfirmDeleteValidationModal();
              toggleModalActivities();
              requestGetAllPersonValidatedActivities({
                id,
                courseHashId: selectedCourse?.value || '',
                organizationHashId: selectedOrganization?.value || '',
                ...(selectedGroups.length > 0 && {
                  ['groupHashIds[]']: selectedGroups.map(
                    (group) => group.value,
                  ),
                }),
                startsAt: period[0].toISOString(),
                endsAt: period[1].toISOString(),
              });
            },
          )
        }
        actionBtnLabel="Anular"
        loading={loadingDeleteActivityValidation}
      />
    </Col>
  );
};
